/**
 * "Ela chegou" — deciding when the story of a unit's woman should present
 * itself on the journey wall, and the small mappings the viewer needs.
 *
 * The story opens from the unit's arrival banner ("meet her", see
 * `lib/arrival.ts` for when that banner shows) or from a poster's portrait,
 * never while the seal ceremony or the journey celebration own the screen.
 * Everything here is pure so the rules can be tested without the (large)
 * journey page.
 */

export interface StoryCandidate {
  unitId: string;
  status: string;
  locked?: boolean | null;
  story?: { available: boolean; seenAt: string | Date | null } | null;
}

/**
 * Whether something else owns the screen (or is about to): a pending
 * `?ceremony=` request, the ceremony itself, the journey celebration, the
 * automatic jump to the next journey, or a wall that is read-only. The story
 * never competes with these — it comes after they have closed.
 */
export function isStoryFlowBlocked(opts: {
  ceremonyParam?: string | null;
  ceremonyActive: boolean;
  celebrationPending: boolean;
  autoNextPending?: boolean;
  readOnly?: boolean;
}): boolean {
  return (
    !!opts.ceremonyParam ||
    opts.ceremonyActive ||
    opts.celebrationPending ||
    !!opts.autoNextPending ||
    !!opts.readOnly
  );
}

/** Whether tapping her portrait on the poster may open the story. */
export function canOpenStory(unit: StoryCandidate | null | undefined): boolean {
  return !!unit && !unit.locked && !!unit.story?.available;
}

/** The app language (`en`, `en-US`, `pt`, `pt-BR`…) as the viewer's 'en' | 'pt'. */
export function normalizeStoryLang(language: string | null | undefined): 'en' | 'pt' {
  return (language ?? '').toLowerCase().startsWith('pt') ? 'pt' : 'en';
}

/**
 * The wall cycles six poster styles, the story viewer knows the first four
 * (red, pale pink, black, pink). The two extra wall styles map to their
 * nearest sibling: sizzling red → red, cream (red ink) → pale pink (red ink).
 */
const WALL_TO_STORY_POSTER_STYLE = [0, 1, 2, 3, 0, 1] as const;

export function storyPosterStyleIndex(wallIndex: number): number {
  const n = WALL_TO_STORY_POSTER_STYLE.length;
  const i = Number.isFinite(wallIndex) ? Math.trunc(wallIndex) : 0;
  return WALL_TO_STORY_POSTER_STYLE[((i % n) + n) % n]!;
}

/** First name, for "MEET {NAME}". */
export function storyFirstName(name: string | null | undefined): string {
  return (name ?? '').trim().split(/\s+/)[0] ?? '';
}
